import {format, isValid} from 'date-fns';
import {ClientProfile, EncounterType} from '@/api/dto/client-profile';
import {BasicUser, User} from "@/api/dto/dto";

// Flowable Workflow is a temporary event type until we get workflows (and event types) defined by product
// https://hbuco.atlassian.net/browse/CARECO-4605
export enum CareJourneyEventType {
  TCM_REFERRAL = 'TCM_REFERRAL',
  ADT_ER_BH = 'ADT_ER_BH',
  ADT_3YR_ER_BH = 'ADT_3YR_ER_BH',
  ADT_3YR_IP_BH = 'ADT_3YR_IP_BH',
  ADT_IP_BH_PLUS_PCP = 'ADT_IP_BH_PLUS_PCP',
  ADT_NONROSTER_IP_BH = 'ADT_NONROSTER_IP_BH',
  ADT_NONROSTER_ER_BH = 'ADT_NONROSTER_ER_BH',
  FLOWABLE_WORKFLOW = 'FLOWABLE_WORKFLOW',
  TCM_BH_REFERRAL = 'TCM_BH_REFERRAL',
  BH_SCHEDULE_APPOINTMENT = 'BH_SCHEDULE_APPOINTMENT',
  BH_APPOINTMENT = 'BH_APPOINTMENT'
}

export enum CareJourneyType {
  TCM = "TCM",
  TCM_BH_REFERRAL = "TCM_BH_REFERRAL",
  BH_JOURNEY = "BH_JOURNEY",
  BH_SCHEDULE_APPOINTMENT = "BH_SCHEDULE_APPOINTMENT"
}

export enum CareJourneyPriority {
  URGENT = 'URGENT',
}

export enum CareJourneyAttachmentType {
  USER_UPLOAD = 'USER_UPLOAD',
  DMCS_CARE_NOTE = 'DMCS_CARE_NOTE',
  CARE_TEAM_REFERRAL = 'CARE_TEAM_REFERRAL',
  OUTREACH_ATTEMPTS_SUMMARY = 'OUTREACH_ATTEMPTS_SUMMARY'
}

export enum CareJourneyFaxStatus {
  WAITING = 'WAITING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED',
}

export enum CareJourneyLogType {
  ASSIGNED = 'ASSIGNED',
  UNASSIGNED = 'UNASSIGNED',
  STEP_STARTED = 'STEP_STARTED',
  STEP_COMPLETED = 'STEP_COMPLETED',
  STEP_FAILED = 'STEP_FAILED',
  STEP_SUBMITTED = 'STEP_SUBMITTED',
  CLOSED = 'CLOSED',
  DUE_DATE_CHANGED = 'DUE_DATE_CHANGED',
  PRIORITY_CHANGED = 'PRIORITY_CHANGED',
  FILE_DELIVERED = 'FILE_DELIVERED',
  CARE_TEAM_REFERRAL_SENT = 'CARE_TEAM_REFERRAL_SENT',
  CARE_NOTE_MANUALLY_SENT = 'CARE_NOTE_MANUALLY_SENT',
  UNKNOWN = 'UNKNOWN',
  APPOINTMENT_REBOOK_NEEDED = 'APPOINTMENT_REBOOK_NEEDED',
  APPOINTMENT_CREATED = 'APPOINTMENT_CREATED',
  APPOINTMENT_SEALED = 'APPOINTMENT_SEALED',
  APPOINTMENT_REOPENED = 'APPOINTMENT_REOPENED',
  APPOINTMENT_CANCELLED = 'APPOINTMENT_CANCELLED',
  CAMPAIGN_ENROLLED = 'CAMPAIGN_ENROLLED',
  CAMPAIGN_MANUALLY_REENROLLED = 'CAMPAIGN_MANUALLY_REENROLLED',
  CAMPAIGN_ENROLLMENT_FAILED = 'CAMPAIGN_ENROLLMENT_FAILED',
}

export enum TaskStepName {
  NONE = 'NONE',
  INITIAL_OUTREACH = 'initial_outreach',
  INTAKE = 'intake',
  SCHEDULED_APPOINTMENT = 'schedule_appointment',
  SCHEDULED_APPOINTMENTS = 'schedule_appointments',
  SMS_APPOINTMENT_NOTIFICATION = 'sms_appointment_notification',
  SMS_APPOINTMENT_REMINDER = 'sms_appointment_reminder',
  SMS_APPOINTMENT_CONFIRMATION = 'sms_appointment_confirmation',
  APPOINTMENT_NOTIFICATION = 'appointment_notification',
  APPOINTMENT_CONFIRMATION = 'appointment_confirmation',
  PRE_INITIAL_OUTREACH = 'pre_initial_outreach',
}

export enum DaysOpenValue {
  OneDay = '1_day',
  TwoToThreeDays = '2_3_days',
  FourToFiveDays = '4_5_days',
  SixToSevenDays = '6_7_days',
  MoreThanOneWeek = 'gt_1_week',
  MoreThanTwoWeeks = 'gt_2_weeks',
  MoreThanOneMonth = 'gt_1_month',
  MoreThanThreeMonths = 'gt_3_months',
  MoreThanSixMonths = 'gt_6_months',
  MoreThanOneYear = 'gt_1_year',
}

export enum AttestationStatus {
  NOT_READY = 'NOT_READY',
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  NOT_REQUIRED = 'NOT_REQUIRED',
}

export enum DirectMessageTargetType {
  PROVIDER = 'PROVIDER',
  CARE_TEAM = 'CARE_TEAM',
}

export enum DirectMessageStatus {
  PENDING = 'PENDING',
  DELIVERED = 'DELIVERED',
  FAILED = 'FAILED',
}

export const attestationStatusDisplayValues: Record<AttestationStatus, string> = {
  [AttestationStatus.NOT_READY]: 'Not ready',
  [AttestationStatus.PENDING]: 'Pending',
  [AttestationStatus.COMPLETED]: 'Completed',
  [AttestationStatus.NOT_REQUIRED]: 'Not required',
};

export const taskTypeDisplayValues: Record<CareJourneyEventType, string> = {
  [CareJourneyEventType.TCM_REFERRAL]: 'Transitional care management (TCM)',
  [CareJourneyEventType.TCM_BH_REFERRAL]: 'TCM_BH_REFERRAL',
  [CareJourneyEventType.ADT_ER_BH]: 'ADT_ER_BH',
  [CareJourneyEventType.ADT_3YR_ER_BH]: 'ADT_3YR_ER_BH',
  [CareJourneyEventType.ADT_3YR_IP_BH]: 'ADT_3YR_IP_BH',
  [CareJourneyEventType.ADT_IP_BH_PLUS_PCP]: 'ADT_IP_BH_PLUS_PCP',
  [CareJourneyEventType.ADT_NONROSTER_IP_BH]: 'ADT_NONROSTER_IP_BH',
  [CareJourneyEventType.ADT_NONROSTER_ER_BH]: 'ADT_NONROSTER_ER_BH',
  [CareJourneyEventType.FLOWABLE_WORKFLOW]: 'Flowable workflow',

  // Not user-facing event
  [CareJourneyEventType.BH_SCHEDULE_APPOINTMENT]: 'SCHEDULE_APPOINTMENT',
  [CareJourneyEventType.BH_APPOINTMENT]: 'BH_APPOINTMENT',
};

export const taskStatusDisplayValues : Record<string, string> = {
  active: 'In progress',
  closed: 'Closed',
  cancelled: 'Closed',
  completed: 'Completed',
  skipped: 'Skipped'
};

export interface CareJourneyResponse {
  careJourneyInstanceId: string;
  rosterId?: number;
  journeySource: 'TCM' | 'FLOWABLE';
  triggeringEventType: EncounterType;
  triggeringEventDateTime: string;
  eventType: CareJourneyEventType;
  eventSetting: string | null;
  priority: CareJourneyPriority | null;
  lastCompletedStep: string | null;
  nextStep: string | null;
  lastViewedAt: string | null;
  dueDate: string;
  status: string | null;
  patientId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  userAssignment: CareJourneyUserAssignment;
  groupName: string;
  organizationName: string | null;
}

export interface CareJourneyResponses {
  searchTotal: number,
  rosterTotal: number,
  careJourneys: Array<CareJourneyResponse>
}

export enum JourneySource {
  TCM = 'TCM',
  FLOWABLE = 'FLOWABLE',
}

export enum JourneyInitiationMode {
  AI = 'AI',
  HUMAN_ONLY = 'HUMAN_ONLY',
  HITL = 'HITL',
}

export enum InitiationReason {
  HIGH_RISK = 'HIGH_RISK',
  NEEDS_EXCLUSION_REVIEW = 'NEEDS_EXCLUSION_REVIEW',
  CUSTOMER_DEFINED_HITL = 'CUSTOMER_DEFINED_HITL',
  CUSTOMER_DEFINED_HUMAN_ONLY = 'CUSTOMER_DEFINED_HUMAN_ONLY',
}

export interface OrchestrationEngineDetails {
  actor: string;
  unassignedByAi: boolean;
  reason: string | null;
  reasonStep: string | null;
  // Appointment sub-journeys only; mapped from metadata.reason in OE response
  closeReason?: string | null;
  appointmentSealed?: boolean;
  attestation?: Attestation;
  directMessageDeliveries: DirectMessageDelivery[];
  rawResponse?: Record<string, unknown>;
  initiationReasons: InitiationReason[];
  initiationMode: JourneyInitiationMode | null;
}

// Will be populated as CWE features ship (e.g., workflow metadata, form bindings)
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CweDetails {}

export interface EscalatedByUser {
  id: string;
  first_name: string;
  last_name: string;
}

export interface AdditionalEvent {
  referralId: string;
  dataType: string | null;
  rejectReason: string | null;
  eventDateTime: string | null;
  triggeringEncounterId: string | null;
  createdAt: string;
  noteThreadId: number | null;
}

export interface CareJourneyDetailsResponse {
  careJourneyInstanceId: string;
  journeySource: JourneySource;
  eventType: CareJourneyEventType;
  eventSetting: string | null;
  priority: CareJourneyPriority | null;
  dueDate: string;
  status: string;
  triggeringEncounterDateTime: string;
  clientProfile: ClientProfile;
  journeyType: string;
  currentStep: string;
  organizationId?: number | null;
  steps: CareJourneyStep[];
  attachments: CareJourneyAttachment[];
  logs: CareJourneyLog[];
  eventLogs: CareJourneyEventLog[];
  userAssignment: CareJourneyUserAssignment;
  orchestrationEngineDetails?: OrchestrationEngineDetails;
  cweDetails?: CweDetails;
  escalatedToCareTeam: boolean;
  escalationReason?: EscalationReason | null;
  escalatedBy?: EscalatedByUser | null;
  escalatedAt?: string | null;
}

export enum EscalationReason {
  INVALID_OR_DISCONNECTED_PHONE_NUMBER = 'INVALID_OR_DISCONNECTED_PHONE_NUMBER',
  WRONG_NUMBER_OR_IDENTITY_MISMATCH = 'WRONG_NUMBER_OR_IDENTITY_MISMATCH',
  INCORRECT_PCP = 'INCORRECT_PCP',
  MEMBER_HANGS_UP_OR_REFUSES_INTERACTION = 'MEMBER_HANGS_UP_OR_REFUSES_INTERACTION',
  LANGUAGE_BARRIER_BEYOND_AI_CAPABILITY = 'LANGUAGE_BARRIER_BEYOND_AI_CAPABILITY',
  HEARING_SPEECH_IMPAIRMENTS = 'HEARING_SPEECH_IMPAIRMENTS',
  MEMBER_ASKS_CLINICAL_QUESTIONS_OR_HAS_CHALLENGES = 'MEMBER_ASKS_CLINICAL_QUESTIONS_OR_HAS_CHALLENGES',
  MEMBER_MENTIONS_BARRIER_TO_APPOINTMENT = 'MEMBER_MENTIONS_BARRIER_TO_APPOINTMENT',
  REQUEST_TO_SPEAK_WITH_KNOWN_PROVIDER_OR_COORDINATOR = 'REQUEST_TO_SPEAK_WITH_KNOWN_PROVIDER_OR_COORDINATOR',
  MEMBER_EXPRESSES_MEDICAL_EMERGENCY = 'MEMBER_EXPRESSES_MEDICAL_EMERGENCY',
  MEMBER_EXPRESSES_SUICIDAL_THOUGHTS = 'MEMBER_EXPRESSES_SUICIDAL_THOUGHTS',
  UNABLE_TO_SCHEDULE_IN_APPROVED_WINDOW = 'UNABLE_TO_SCHEDULE_IN_APPROVED_WINDOW',
}

export const escalationReasonDisplayValues: Record<EscalationReason, string> = {
  [EscalationReason.INVALID_OR_DISCONNECTED_PHONE_NUMBER]: 'Invalid or disconnected phone number',
  [EscalationReason.WRONG_NUMBER_OR_IDENTITY_MISMATCH]: 'Wrong number or identity mismatch',
  [EscalationReason.INCORRECT_PCP]: 'Incorrect PCP',
  [EscalationReason.MEMBER_HANGS_UP_OR_REFUSES_INTERACTION]: 'Member hangs up repeatedly or refuses interaction',
  [EscalationReason.LANGUAGE_BARRIER_BEYOND_AI_CAPABILITY]: 'Language barrier beyond AI capability',
  [EscalationReason.HEARING_SPEECH_IMPAIRMENTS]: 'Hearing/speech impairments affecting call quality',
  [EscalationReason.MEMBER_ASKS_CLINICAL_QUESTIONS_OR_HAS_CHALLENGES]: 'Member asks clinical questions or has challenges',
  [EscalationReason.MEMBER_MENTIONS_BARRIER_TO_APPOINTMENT]: 'Member mentions a barrier to attending the appointment',
  [EscalationReason.REQUEST_TO_SPEAK_WITH_KNOWN_PROVIDER_OR_COORDINATOR]:
    'Request to speak with a known provider or care coordinator',
  [EscalationReason.MEMBER_EXPRESSES_MEDICAL_EMERGENCY]: 'Member expresses that they are suffering from a medical emergency',
  [EscalationReason.MEMBER_EXPRESSES_SUICIDAL_THOUGHTS]: 'Member expresses suicidal thoughts',
  [EscalationReason.UNABLE_TO_SCHEDULE_IN_APPROVED_WINDOW]: 'Unable to schedule appointment in approved window',
};

export interface ScheduleAppointmentJourneyResponse extends CareJourneyDetailsResponse {
  careJourneyInstanceId: string;
  practiceName?: string;
  telephoneNumber?: string;
  practiceTelephone?: string;
  appointmentType: string;
  providerType: string;
  parentJourneyId?: string;
  encounterId?: string;
  providerName?: string;
}

export const ModuleType = {
  SEND_MESSAGE: 'SEND_MESSAGE',
  COMPLETE_FORM: 'COMPLETE_FORM',
};

export interface BaseCareJourneyStep {
  stepName: string;
  displayLabel?: string;
  assigneeType: string | null;
  status: string;
  statusReason?: string;
  surveyJson?: string;
  schemaGuid: string | null;
  attempts: Record<string, any>[];
  moduleType?: string;
  formKey?: string;
  taskId?: string;
  formData?: string;
  skippable?: boolean;
  skipped?: boolean;
}

export interface CareJourneyStep extends BaseCareJourneyStep{
  appointments?: ScheduleAppointmentJourneyResponse[];
}

export interface CareJourneyAttachment {
  id: string;
  s3Key?: string;
  type: CareJourneyAttachmentType;
  dmcsDocId?: string;
  createdBy?: User;
  createdAt: string;
  fax?: CareJourneyFax;
  fileName?: string;
}

export interface CareJourneyFax {
  fax?: string;
  externalId?: string;
  externalStatus?: CareJourneyFaxStatus;
  externalFailReason?: string;
  sentAt?: string;
  statusUpdatedAt?: string;
}

export interface CareJourneyLog {
  id: number;
  user?: User;
  eventDatetime: string;
  logType: CareJourneyLogType;
  systemOriginated: boolean;
  stepName: string;
  reason?: string;
  dueDate?: string;
  priority?: CareJourneyPriority;
}

export interface CareJourneyEventLog {
  messageId: string;
  messageBody: string;
  eventDatetime: string;
  eventType: string;
  stepName: string;
}

export interface CareJourneyRequest {
  priority: CareJourneyPriority | null;
  dueDate: string | null;
}

export interface CareJourneyUserAssignment {
  assignedToAi: boolean,
  users?: User[]
}


export interface Attestation {
  status: AttestationStatus;
  attestedBy?: {
    firstName: string;
    lastName: string;
    title?: string;
  };
  attestedAt?: string;
}

export interface DirectMessageDelivery {
  targetType: DirectMessageTargetType;
  directAddress: string;
  status: DirectMessageStatus;
  attachmentId: string
  failReason: string | null;
  deliveredAt: string | null;
  createdAt: string;
  initiatedBy?: BasicUser
}

export interface CareJourneyFilters {
  statuses?: string[],
  priorities?: string[],
  eventTypes?: string[],
  daysOpen?: string[],
  daysSinceDischarge?: string[],
  taskStartDate?: string | null,
  taskEndDate?: string | null,
  taskTypes?: string[],
  dueStartDate?: string | null,
  dueEndDate?: string | null,
  nextSteps?: string[],
  lastViewedStartDate?: string | null,
  lastViewedEndDate?: string | null,
  lastCompletedSteps?: string[],
  assignees?: string[],
  attestationStatuses?: string[],
  sortBy?: string,
  sortOrder?: string,
  searchName?: string | null,
  searchTaskId?: string | null,
  timeZone?: string;
  groups?: string[],
  rosterIds?: number[],
}

const DATE_RANGE_KEYS: Record<string, [string, string]> = {
  taskDate: ['taskStartDate', 'taskEndDate'],
  dueDate: ['dueStartDate', 'dueEndDate'],
  lastViewed: ['lastViewedStartDate', 'lastViewedEndDate'],
};

const formatDate = (d: any): string | null => {
  if (!d) return null;
  const parsed = new Date(d);
  return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : null;
};

export function formatTaskFiltersForApi(
  params: Record<string, any>,
): CareJourneyFilters {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    if (key in DATE_RANGE_KEYS) {
      const [startKey, endKey] = DATE_RANGE_KEYS[key];
      result[startKey] = formatDate(value?.startDate);
      result[endKey] = formatDate(value?.endDate);
    } else if (key === 'groups') {
      result['rosterIds'] = Array.isArray(value) && value.length > 0
        ? value.map((v: string) => Number(v)).filter((n: number) => !isNaN(n))
        : undefined;
    } else {
      result[key] = value;
    }
  }
  return result as CareJourneyFilters;
}

/**
 * True once the journey's engine-specific details have loaded. OE (TCM) journeys
 * expose `orchestrationEngineDetails`; CWE (FLOWABLE) journeys expose `cweDetails`.
 * Actions that work across both engines (e.g. Close) should gate on this rather
 * than on `orchestrationEngineDetails` alone, which is always absent for CWE.
 */
export function hasJourneyEngineDetails(journey: CareJourneyDetailsResponse): boolean {
  return Boolean(journey.orchestrationEngineDetails) || Boolean(journey.cweDetails);
}

export interface TaskSummaryRequest {
  pinnedFilters: { id: number; filters: CareJourneyFilters }[];
}

export interface TaskSummaryResponse {
  currentUserUuid: string;
  assignedTaskCount: number;
  overdueTaskCount: number;
  pinnedFilterCounts: { id: number; count: number }[];
}

export interface ScheduleAppointmentRequest {
  customerId: string;
  journeyType: string;
  parentJourneyId: string;
}

export interface ScheduleAppointmentAiRequest extends ScheduleAppointmentRequest{
  immediate?: boolean;
  providerOrganization: {
    name: string;
    phoneNumber?: string;
    address?: { street1?: string; city?: string; state?: string; zip?: string };
  };
  primaryCareProvider?: {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    npi?: string;
  };
  appointmentType: string;
  providerType: string;
}

export interface ScheduleAppointmentHumanRequest extends ScheduleAppointmentRequest {
  // eslint-disable-next-line max-len
  // https://hbuco.atlassian.net/wiki/spaces/PE/pages/120348573707/Manual+AI+Appointment+Scheduling+API+Integration+Spec#4.1-Confirmation-channel-%E2%80%94-channel_selection-vs-force_hitl
  confirmationChannel: 'channel_selection' | 'force_hitl';
  appointmentType: string,
  providerType: string,
  appointmentDateTime: string;
  appointmentSetting: string;
  scheduledWith: string;
  location: string;
  form: Record<string, any>;
}

export interface ScheduleAppointmentResponse {
  journey_id: string;
  appointmentRequestId: string;
  message: string;
}

export interface SealAppointmentRequest {
  journey_id: string;
  sealed: boolean;
  sealed_by: string;
  reason?: string;
}

export interface CancelAppointmentRequest {
  journey_id: string;
  disposition: 'Cancelled' | 'Closed';
  canceledBy: string;
  reason: string;
}
